import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { IoCloseSharp } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { useTranslation } from "react-i18next";
import { useGetAllVehiclesQuery } from "../../../store/api/vendor/vehiclesSlice";
import {
  useAddMaintainanceMutation,
  useGetMaintainanceDetailsQuery,
  useUpdateMaintainanceMutation,
} from "../../../store/api/vendor/MaintainanceSlice";
import { formatDateToYMD } from "../../../utils/dateAndTimeConversion";
import CreateModal from "../../../component/Model/CreateModel";
import CustomDatePicker from "../../../component/CustomDatePicker";
import { getTomorrow } from "../Driver/AddSchedule";
import AddMaintenanceSkeleton from "../../../component/skelton/AddMaintenanceSkeleton";

const initialState = {
  vehicle_id: "",
  maintenance_date: null,
  expected_date: null,
  km_miles: "",
  maintenance_type: "",
  description: "",
  cost: "",
};

const AddMaintenanceInspection = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(initialState);
  const [vehicles, setVehicles] = useState([]);
  const [errors, setErrors] = useState({});
  const [show, setShow] = useState(false);
  const navigate = useNavigate();
  const tomorrow = getTomorrow();
  const { id, role } = useParams();

  // Fetch all vehicles
  const { data, isSuccess } = useGetAllVehiclesQuery({});

  // Add Maintainance
  const [createMaintainance, { isLoading: isCreateLoading }] =
    useAddMaintainanceMutation();

  // Update Maintainance
  const [updateMaintainance, { isLoading: isUpdateLoading }] =
    useUpdateMaintainanceMutation();

  // Get route schedule details for update
  const {
    data: MaintainanceDetailData,
    isSuccess: isSuccessMaintainanceDetail,
    isLoading,
  } = useGetMaintainanceDetailsQuery(id, {
    skip: !id,
  });

  useEffect(() => {
    if (isSuccess && data?.data) {
      setVehicles(data?.data?.vehicles);
    }
  }, [isSuccess, data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({
      ...prev,
      [name]: value ? "" : `${t("errors." + name)} ${t("errors.required")}`,
    }));
  };

  const handleDateChange = (date, fieldName) => {
    setFormData((prev) => {
      if (
        fieldName === "maintenance_date" &&
        prev.expected_date &&
        date > prev.expected_date
      ) {
        return {
          ...prev,
          maintenance_date: date,
          expected_date: null,
        };
      }

      return {
        ...prev,
        [fieldName]: date,
      };
    });
  };

  const validateForm = () => {
    const newErrors = {};

    Object.entries(formData).forEach(([key, value]) => {
      if (key === "description") return;

      if (typeof value === "object" && value !== null) {
        // Handle nested address object
        Object.entries(value).forEach(([subKey, subValue]) => {
          if (!subValue || subValue?.trim() === "") {
            newErrors[key] = `${t("errors." + key)} ${t("errors.required")}`;
          }
        });
      } else {
        if (
          value === null ||
          value === undefined ||
          (typeof value === "string" && value.trim() === "")
        ) {
          newErrors[key] = `${t("errors." + key)} ${t("errors.required")}`;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    toast.dismiss();
    try {
      let response;

      const payload = {
        ...formData,
        maintenance_date: formatDateToYMD(formData.maintenance_date),
        expected_date: formatDateToYMD(formData.expected_date),
      };

      if (id) {
        response = await updateMaintainance({ data: payload, id }).unwrap();
      } else {
        response = await createMaintainance(payload).unwrap();
      }

      if (response.statusCode === 200 || response.statusCode === 201) {
        setShow(true);
      }
    } catch (err) {
      const message =
        err?.data?.message || err.message || "Something went wrong!";
      toast.error(message);
    }
  };

  useEffect(() => {
    if (isSuccessMaintainanceDetail && MaintainanceDetailData?.data) {
      const formattedDate = MaintainanceDetailData?.data?.maintenance_date
        ? new Date(MaintainanceDetailData.data.maintenance_date)
        : "";
      const formattedExpectedDate = MaintainanceDetailData?.data?.expected_date
        ? new Date(MaintainanceDetailData.data.expected_date)
        : "";

      setFormData({
        vehicle_id: MaintainanceDetailData?.data?.vehicle.id || "",
        maintenance_date: formattedDate || "",
        expected_date: formattedExpectedDate || "",
        km_miles: MaintainanceDetailData?.data?.km_miles || "",
        maintenance_type: MaintainanceDetailData?.data?.maintenance_type || "",
        description: MaintainanceDetailData?.data?.description || "",
        cost: MaintainanceDetailData?.data?.cost || "",
      });
    }
  }, [MaintainanceDetailData, isSuccessMaintainanceDetail]);

  const handleReset = () => {
    setFormData(initialState);
  };

  const handleClose = () => {
    setShow(false);
    navigate(`/maintenance-and-inspection`);
  };

  return (
    <>
      <div className="outerBox">
        <div className="d-flex mb-3">
          <button className="page-back-btn" onClick={() => navigate(-1)}>
            ← {t("back")}
          </button>
        </div>
        {/* <div className="dbSubHd mb-3">Seat Availability Info</div> */}
        <div className="dbSubHd mb-4">
          {t("maintenance")} {t("details")}
        </div>

        {isLoading ? (
          // Skeleton Loading State
          <AddMaintenanceSkeleton />
        ) : (
          <>
            <ul className="form d-flex flex-wrap p-0">
              <li className="col-12 col-md-4">
                <label className="form-label fw-semibold">
                  {`${t("vehicles")}`} <span className="text-danger">*</span>
                </label>
                <select
                  className="formSelect"
                  value={formData.vehicle_id}
                  onChange={handleChange}
                  name="vehicle_id"
                >
                  <option value="">{`${t("select")} ${t("vehicle")}`}</option>
                  {vehicles.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.vehicle_type.name} ({item.license_number})
                    </option>
                  ))}
                </select>
                {errors?.vehicle_id && (
                  <span className="text-danger small">{errors.vehicle_id}</span>
                )}
              </li>
              <li className="col-12 col-md-4">
                <label className="form-label fw-semibold">
                  {`${t("vehicle")} ${t("type")}`}{" "}
                  <span className="text-danger">*</span>
                </label>
                <select
                  className="formSelect"
                  value={formData.maintenance_type}
                  onChange={handleChange}
                  name="maintenance_type"
                >
                  <option value="">{`${t("select")} ${t("vehicle")} ${t(
                    "type",
                  )}`}</option>
                  <option value="routine">Routine</option>
                  <option value="repair">Repair</option>
                  <option value="inspection">{t("inspection")}</option>
                  <option value="emergency">Emergency</option>
                  {/* <option value="maintenance">{t("maintenance")}</option> */}
                </select>
                {errors?.maintenance_type && (
                  <span className="text-danger small">
                    {errors.maintenance_type}
                  </span>
                )}
              </li>
              <li className="col-12 col-md-4">
                <label className="form-label fw-semibold">
                  {`${t("km")}/${t("miles")}`}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="formInput"
                  name="km_miles"
                  placeholder={`${t("km")}/${t("miles")}`}
                  value={formData.km_miles}
                  onChange={handleChange}
                />
                {errors?.km_miles && (
                  <span className="text-danger small">{errors.km_miles}</span>
                )}
              </li>
              {/* Date Field */}
              <li className="col-12 col-md-4">
                <CustomDatePicker
                  label={`${t("maintenance")} ${t("date")}`}
                  selected={formData.maintenance_date}
                  onChange={(date) =>
                    handleDateChange(date, "maintenance_date")
                  }
                  minDate={tomorrow}
                  error={errors.maintenance_date}
                  className="formInput"
                />
              </li>
              {/* Expected Date Field */}
              <li className="col-12 col-md-4">
                <CustomDatePicker
                  label={`${t("expected")} ${t("date")}`}
                  selected={formData.expected_date}
                  onChange={(date) => handleDateChange(date, "expected_date")}
                  minDate={formData.maintenance_date || tomorrow}
                  error={errors.expected_date}
                  className="formInput"
                />
              </li>

              <li className="col-12 col-md-4">
                <label className="form-label fw-semibold">
                  {`${t("cost")}`} <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className="formInput"
                  name="cost"
                  placeholder={t("cost")}
                  value={formData.cost}
                  onChange={handleChange}
                />
                {errors?.cost && (
                  <span className="text-danger small">{errors.cost}</span>
                )}
              </li>

              {/* Notes */}
              <li className="col-12 mb-4">
                <label className="form-label fw-semibold">
                  {t("description")}
                </label>
                <textarea
                  className="formInput"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="4"
                  style={{ width: "100%" }}
                  placeholder={`${t("add")} ${t("any")} ${t("additional")} ${t("notes")} ${t("or")} ${t("instructions")}...`}
                />
              </li>
            </ul>
            {/* Buttons */}
            <div className="pt-4 mt-3 border-top text-end">
              <Button
                variant="outline-secondary"
                onClick={handleReset}
                className="me-3 px-4 py-2"
              >
                {t("btns.reset")}
              </Button>
              <Button
                variant="primary"
                type="button"
                onClick={handleSubmit}
                disabled={isCreateLoading || isUpdateLoading}
                className="px-4 py-2"
              >
                {isCreateLoading || isUpdateLoading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </span>
                  </>
                ) : (
                  t("btns.submit")
                )}
              </Button>
            </div>
          </>
        )}
      </div>

      <Modal show={show}>
        <Button className="closePopup" onClick={handleClose}>
          <IoCloseSharp />
        </Button>
        <CreateModal
          title={`${t("maintenance")} ${t("and")} ${t("inspection")} ${
            id ? t("updated") : t("added")
          } ${t("successfully")}!`}
          text={
            id
              ? `${t("maintenance")} ${t("and")} ${t("inspection")} ${t(
                  "has",
                )} ${t("been")} ${t("updated")}.`
              : `${t("maintenance")} ${t("and")} ${t("inspection")} ${t(
                  "has",
                )} ${t("been")} ${t("added")}.`
          }
          onClose={handleClose}
        />
      </Modal>
    </>
  );
};

export default AddMaintenanceInspection;
